import { anonymousHttpClient, httpClient } from "../../../lib/http/axios";
import { createMainData } from "./itineraryMainDataRepo";
export { createMainData };
import type { ApiEnvelope } from "../../../lib/http/contracts";
import type { components } from "../../../api/generated";
import {
  mapDrivingRequest,
  mapDrivingResult,
  mapEditorDocument,
  mapEnrichedContent,
  mapEnrichRequest,
  mapEnterResult,
  mapFlightQuery,
  mapFlightResult,
  mapItineraryList,
  mapItineraryCustomerOutput,
  mapItineraryCopyResult,
  mapItineraryMoveTargets,
  mapItineraryCopyTargetPage,
  mapItineraryInternalInteractions,
  mapItineraryInternalMarkResult,
  mapMajorCandidateQuery,
  mapMajorCandidates,
  mapParsedDay,
  mapParseDayRequest,
  mapPointDescriptionCompletionRequest,
  mapPointDescriptionCompletionResult,
  mapPointCandidatePage,
  mapPointCandidateQuery,
  mapPublicItinerary,
  mapItineraryReadonlyPreview,
  mapRailwayQuery,
  mapRailwayResult,
  mapRailwayStationResolution,
  mapRailwayStationResolveRequest,
  mapSaveRequest,
  mapShareLink,
  mapItinerarySuggestion,
  mapItinerarySalesNotification,
  mapValidateRequest,
  mapValidationResult,
  mapWalkingRequest,
  mapWalkingResult,
  type DrivingResponseRaw,
  type EditorResponseRaw,
  type EnrichedContentResponseRaw,
  type EnterResponseRaw,
  type FlightSearchResponseRaw,
  type ListResponseRaw,
  type MajorCandidateResponseRaw,
  type ParsedDayResponseRaw,
  type PointDescriptionCompletionResponseRaw,
  type PointCandidateResponseRaw,
  type PublicItineraryResponseRaw,
  type ItineraryReadonlyPreviewResponseRaw,
  type ItineraryInternalInteractionsResponseRaw,
  type ItineraryInternalMarkResponseRaw,
  type ItinerarySuggestionResponseRaw,
  type ItineraryCustomerOutputResponseRaw,
  type ItineraryCopyResponseRaw,
  type ItineraryMoveResponseRaw,
  type ItineraryMoveTargetsResponseRaw,
  type ItineraryCopyTargetResponseRaw,
  type RailwaySearchResponseRaw,
  type RailwayStationResolveResponseRaw,
  type ShareLinkResponseRaw,
  type ValidateResponseRaw,
  type WalkingResponseRaw,
  type ItinerarySalesNotificationRaw,
} from "../api/adapters";
import type {
  DrivingRequest,
  DrivingResult,
  EnrichedContent,
  EnrichRequest,
  FlightQuery,
  FlightSearchResult,
  ItineraryEditor,
  ItineraryCustomerOutput,
  ItineraryCustomizationInfo,
  ItineraryEditorDocument,
  ItineraryEnterResult,
  ItineraryCopyResult,
  ItineraryMoveResult,
  ItineraryMoveTargets,
  ItineraryCopyTargetPage,
  ItineraryListResult,
  ItineraryInternalInteractions,
  ItineraryInternalMarkResult,
  ItinerarySaveRequest,
  ItineraryValidationResult,
  ItineraryMarkType,
  ItinerarySuggestion,
  MajorCandidate,
  MajorCandidateQuery,
  StaffUserCandidate,
  StaffUserCandidateQuery,
  ParseDayRequest,
  ParsedDayDraft,
  PointDescriptionCompletionRequest,
  PointDescriptionCompletionResult,
  PointCandidateResult,
  PointCandidateQuery,
  PublicItinerary,
  ItineraryReadonlyPreview,
  RailwayQuery,
  RailwaySearchResult,
  RailwayStationResolution,
  RailwayStationResolveRequest,
  ShareLinkResult,
  WalkingRequest,
  WalkingResult,
  ItinerarySalesNotificationResult,
} from "../types";

const EDITOR_SAVE_TIMEOUT_MS = 180_000;

type StaffUserCandidateRaw = {
  user_id: string;
  display_name: string;
};

/** 将销售/计调人员候选接口字段映射为前端 camelCase. */
function mapStaffUserCandidates(raw: StaffUserCandidateRaw[]): StaffUserCandidate[] {
  return raw.map((item) => ({
    userId: item.user_id,
    displayName: item.display_name,
  }));
}

type ItineraryCustomizationInfoResponseRaw = components["schemas"]["ItineraryCustomizationInfoResponse"];

/** 只读查询最新保存版本的旧库同步结果，不暴露任务错误原文。 */
export async function getItineraryLegacySyncStatus(itineraryId: string) {
  const response = await httpClient.get<components["schemas"]["ItineraryLegacySyncStatusResponse"]>(
    `/itineraries/${encodeURIComponent(itineraryId)}/legacy-sync-status`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("旧系统同步状态接口返回了无效数据");
  }
  return response.data.data;
}

/** 读取行程编辑右栏的定制信息；后端在单条 SQL 中完成聚合。 */
export async function getItineraryCustomizationInfo(
  itineraryId: string,
  draftDemandId?: string,
): Promise<ItineraryCustomizationInfo> {
  const response = await httpClient.get<ItineraryCustomizationInfoResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/customization-info`,
    ...(draftDemandId ? [{ params: draftParams(draftDemandId) }] as const : []),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("行程定制信息接口返回了无效数据");
  }
  const raw = response.data.data;
  if (typeof raw.customization_linked !== "boolean" || typeof raw.opportunity_linked !== "boolean") {
    throw new Error("行程关联状态接口返回了无效数据");
  }
  return {
    title: raw.title,
    rows: raw.rows ?? [],
    customizationLinked: raw.customization_linked,
    opportunityLinked: raw.opportunity_linked,
  };
}

/** 幂等进入需求当前行程。 */
export async function enterDemand(demandId: string): Promise<ItineraryEnterResult> {
  const response = await httpClient.post<EnterResponseRaw>(
    `/itineraries/demands/${encodeURIComponent(demandId)}/enter`,
  );
  return mapEnterResult(response.data.data);
}

/** 查询同需求的行程版本。 */
export async function listItineraries(demandId: string): Promise<ItineraryListResult> {
  const response = await httpClient.get<ListResponseRaw>(
    `/itineraries/by-demand/${encodeURIComponent(demandId)}`,
  );
  return mapItineraryList(response.data.data);
}

/** 向课程线路所属项目的销售人员发送微信通知。 */
export async function notifyItinerarySales(
  projectId: string,
  itineraryId: string,
): Promise<ItinerarySalesNotificationResult> {
  const response = await httpClient.post<ApiEnvelope<ItinerarySalesNotificationRaw>>(
    `/customization/projects/${encodeURIComponent(projectId)}/sales-notification`,
    { itinerary_id: itineraryId },
    { "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("课程线路销售通知接口返回了无效数据");
  }
  return mapItinerarySalesNotification(response.data.data);
}

/** 单次查询倒序最多 50 条未关联行程的定制版本候选。 */
export async function listItineraryCopyTargets(query: {
  keyword?: string;
}, signal?: AbortSignal): Promise<ItineraryCopyTargetPage> {
  const response = await httpClient.get<ItineraryCopyTargetResponseRaw>(
    "/itineraries/copy-targets",
    {
      params: { keyword: query.keyword },
      signal,
      timeout: 15_000,
      "axios-retry": { retries: 0 },
    },
  );
  return mapItineraryCopyTargetPage(response.data.data);
}

/** 把服务器最近保存的行程复制到目标定制版本。 */
export async function copyItinerary(
  itineraryId: string,
  targetDemandId: string,
  replaceExisting = false,
): Promise<ItineraryCopyResult> {
  const response = await httpClient.post<ItineraryCopyResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/copy`,
    { target_demand_id: targetDemandId, replace_existing: replaceExisting },
    { "axios-retry": { retries: 0 } },
  );
  return mapItineraryCopyResult(response.data.data);
}

/** 读取当前行程所属定制的其他版本，包含已关联目标。 */
export async function listItineraryMoveTargets(itineraryId: string, signal?: AbortSignal): Promise<ItineraryMoveTargets> {
  const response = await httpClient.get<ItineraryMoveTargetsResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/move-targets`,
    { signal, "axios-retry": { retries: 0 } },
  );
  return mapItineraryMoveTargets(response.data.data);
}

/** 移动原行程版本归属，并保留全部关联单据。 */
export async function moveItinerary(
  itineraryId: string, expectedSourceDemandId: string, targetDemandId: string, replaceExisting = false,
): Promise<ItineraryMoveResult> {
  const response = await httpClient.post<ItineraryMoveResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/move`,
    { expected_source_demand_id: expectedSourceDemandId, target_demand_id: targetDemandId, replace_existing: replaceExisting },
    { "axios-retry": { retries: 0 } },
  );
  return mapItineraryCopyResult(response.data.data);
}

/** 读取完整编辑器快照。 */
export async function getEditor(itineraryId: string, draftDemandId?: string): Promise<ItineraryEditorDocument> {
  const response = await httpClient.get<EditorResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/editor`,
    ...(draftDemandId ? [{ params: draftParams(draftDemandId) }] as const : []),
  );
  const document = mapEditorDocument(response.data.data);
  const payload = await resolveDefaultDayStayAreas(document.payload, false);
  return payload === document.payload ? document : { ...document, payload, requiresPersistedNormalization: true };
}

/** 按名称识别不安排住宿；引用只取当前环境候选，读取阶段不回写服务器。 */
async function resolveDefaultDayStayAreas(editor: ItineraryEditor, required: boolean): Promise<ItineraryEditor> {
  const arrivalStays = new Set(editor.segments.flatMap((segment) => segment.sourcePayload?.railwayOvernight?.stayNodeIds
    .filter((_, index) => index % 2 === 1) ?? []));
  const missing = editor.days.filter((day) => !day.stayAreaId
    && (!day.stayAreaName?.trim() || day.stayAreaName.trim() === "无")
    && !day.nodes.some((node) => node.nodeType === "stay_area" && !arrivalStays.has(node.id)));
  if (!missing.length) return editor;
  let stayId = editor.days.find((day) => day.stayAreaName?.trim() === "无" && day.stayAreaId)?.stayAreaId;
  if (!stayId) {
    try {
      const candidates = await listPointCandidates({ kind: "stay_area", keyword: "无", page: 1, pageSize: 20 });
      stayId = candidates.items.find((item) => item.refType === "stay_area" && item.name.trim() === "无")?.refId;
      if (!stayId) throw new Error("未找到启用的“无”住宿选项，请维护住宿主数据后重试");
    } catch (error) {
      // 默认候选暂不可用不能阻止打开已有行程；主动保存时再解析真实引用。
      if (required) throw error;
    }
  }
  const missingIds = new Set(missing.map((day) => day.id));
  return { ...editor, days: editor.days.map((day) => missingIds.has(day.id)
    ? { ...day, stayAreaId: stayId ?? null, stayAreaName: "无" } : day) };
}

/** 读取允许跳过不可恢复旧路线段的列表预览快照。 */
export async function getPreviewEditor(itineraryId: string): Promise<ItineraryEditorDocument> {
  const response = await httpClient.get<EditorResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/preview-editor`,
  );
  return mapEditorDocument(response.data.data);
}

/** 保存完整快照；revision 仅作为兼容字段随请求传递。 */
export async function saveEditor(
  itineraryId: string,
  request: ItinerarySaveRequest,
  draftDemandId?: string,
): Promise<ItineraryEditorDocument> {
  const payload = await resolveDefaultDayStayAreas(request.payload, true);
  const response = await httpClient.put<EditorResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/editor`,
    mapSaveRequest({ ...request, payload }),
    { ...(draftDemandId ? { params: draftParams(draftDemandId) } : {}), timeout: EDITOR_SAVE_TIMEOUT_MS, "axios-retry": { retries: 0 } },
  );
  return mapEditorDocument(response.data.data);
}

/** 读取与编辑保存隔离的历史事实预览。 */
export async function getReadonlyPreview(itineraryId: string, customerFacing = false): Promise<ItineraryReadonlyPreview> {
  const response = await httpClient.get<ItineraryReadonlyPreviewResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/preview${customerFacing ? "?customer_facing=true" : ""}`,
  );
  return mapItineraryReadonlyPreview(response.data.data);
}

/** 读取仅供内部人员使用的建议、拉黑与收藏状态。 */
export async function getInternalInteractions(
  itineraryId: string,
): Promise<ItineraryInternalInteractions> {
  const response = await httpClient.get<ItineraryInternalInteractionsResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/internal-interactions`,
  );
  return mapItineraryInternalInteractions(response.data.data);
}

/** 切换当前内部用户对行程的拉黑或收藏。 */
export async function toggleInternalMark(
  itineraryId: string,
  markType: ItineraryMarkType,
): Promise<ItineraryInternalMarkResult> {
  const response = await httpClient.post<ItineraryInternalMarkResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/internal-marks/${markType}/toggle`,
  );
  return mapItineraryInternalMarkResult(response.data.data);
}

/** 追加当前内部用户的行程建议或回复。 */
export async function createItinerarySuggestion(
  itineraryId: string,
  content: string,
  parentId?: string | null,
): Promise<ItinerarySuggestion> {
  const response = await httpClient.post<ItinerarySuggestionResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/suggestions`,
    { content, parent_id: parentId ?? undefined },
  );
  return mapItinerarySuggestion(response.data.data);
}

/** 按已保存版本显式切换审稿；响应未知时由页面回读核验，禁止自动重试。 */
export async function setItineraryReview(
  itineraryId: string, revision: string, isReviewing: boolean,
): Promise<ItineraryEditorDocument> {
  const response = await httpClient.put<EditorResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/review`,
    { revision, is_reviewing: isReviewing },
    { "axios-retry": { retries: 0 } },
  );
  return mapEditorDocument(response.data.data);
}

/** 按当前 revision 定稿行程。 */
export async function finalizeItinerary(
  itineraryId: string,
  revision: string,
): Promise<ItineraryEditorDocument> {
  const response = await httpClient.post<EditorResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/finalize`,
    { revision },
  );
  return mapEditorDocument(response.data.data);
}

/** 按当前 revision 取消行程定稿。 */
export async function unfinalizeItinerary(
  itineraryId: string,
  revision: string,
): Promise<ItineraryEditorDocument> {
  const response = await httpClient.post<EditorResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/unfinalize`,
    { revision },
  );
  return mapEditorDocument(response.data.data);
}

/** 使用服务端同款规则校验未保存草稿。 */
export async function validateEditorDraft(
  itineraryId: string,
  editor: ItineraryEditor,
  draftDemandId?: string,
): Promise<ItineraryValidationResult> {
  const response = await httpClient.post<ValidateResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/validate`,
    mapValidateRequest(editor),
    ...(draftDemandId ? [{ params: draftParams(draftDemandId) }] as const : []),
  );
  return mapValidationResult(response.data.data);
}

/** 查询 MySQL 点位候选。 */
export async function listPointCandidates(query: PointCandidateQuery, signal?: AbortSignal): Promise<PointCandidateResult> {
  const response = await httpClient.get<PointCandidateResponseRaw>("/itineraries/candidates/points", {
    params: mapPointCandidateQuery(query),
    ...(query.provinceNames?.length ? { paramsSerializer: { indexes: null } } : {}),
    ...(signal ? { signal } : {}),
    ...((query.kind === "point" || query.kind === "stay_area") && (query.keyword?.trim() || query.recommendation)
      ? { "axios-retry": { retries: 0 } } : {}),
  });
  return mapPointCandidatePage(response.data.data);
}

/** 查询 MySQL 标准专业候选。 */
export async function listMajorCandidates(query: MajorCandidateQuery = {}): Promise<MajorCandidate[]> {
  const response = await httpClient.get<MajorCandidateResponseRaw>("/itineraries/candidates/majors", {
    params: mapMajorCandidateQuery(query),
  });
  return mapMajorCandidates(response.data.data ?? []);
}

/** 查询销售部人员候选，供课程线路列表销售筛选下拉初始加载。 */
export async function listSalesUserCandidates(
  query: StaffUserCandidateQuery = {},
): Promise<StaffUserCandidate[]> {
  const response = await httpClient.get<ApiEnvelope<StaffUserCandidateRaw[]>>(
    "/itineraries/candidates/sales-users",
    { params: { keyword: query.keyword, limit: query.limit ?? 500 } },
  );
  if (response.data.code !== "OK") {
    throw new Error("销售人员候选接口返回了无效数据");
  }
  return mapStaffUserCandidates(response.data.data ?? []);
}

/** 查询计调部人员候选，供课程线路列表计调筛选下拉初始加载。 */
export async function listDispatcherUserCandidates(
  query: StaffUserCandidateQuery = {},
): Promise<StaffUserCandidate[]> {
  const response = await httpClient.get<ApiEnvelope<StaffUserCandidateRaw[]>>(
    "/itineraries/candidates/dispatcher-users",
    { params: { keyword: query.keyword, limit: query.limit ?? 500 } },
  );
  if (response.data.code !== "OK") {
    throw new Error("计调人员候选接口返回了无效数据");
  }
  return mapStaffUserCandidates(response.data.data ?? []);
}

/** 查询 12306 火车候选。 */
export async function getRailwayOptions(query: RailwayQuery, signal?: AbortSignal): Promise<RailwaySearchResult> {
  const response = await httpClient.get<RailwaySearchResponseRaw>("/itineraries/transport/railway", {
    params: mapRailwayQuery(query),
    paramsSerializer: { indexes: null },
    // 区域逐日查询覆盖最多 15 个默认 30 秒窗口，并为接口收尾预留时间。
    ...(query.queryScope !== "station" ? { timeout: 480_000 } : {}),
    "axios-retry": { retries: 0 },
    signal,
  });
  return mapRailwayResult(response.data.data);
}

/** 按业务地点主数据解析 12306 官方火车站候选。 */
export async function resolveRailwayStations(
  request: RailwayStationResolveRequest,
): Promise<RailwayStationResolution> {
  const response = await httpClient.post<RailwayStationResolveResponseRaw>(
    "/itineraries/transport/railway/stations/resolve",
    mapRailwayStationResolveRequest(request),
  );
  return mapRailwayStationResolution(response.data.data);
}

/** 查询 FlyAI 航班候选。 */
export async function getFlightOptions(query: FlightQuery, signal?: AbortSignal): Promise<FlightSearchResult> {
  const response = await httpClient.get<FlightSearchResponseRaw>("/itineraries/transport/flights", {
    params: mapFlightQuery(query),
    signal,
  });
  return mapFlightResult(response.data.data);
}

/** 查询大巴距离，超时覆盖地图与模型两个默认 30 秒窗口。 */
export async function getDrivingRoute(request: DrivingRequest, signal?: AbortSignal): Promise<DrivingResult> {
  const response = await httpClient.post<DrivingResponseRaw>(
    "/itineraries/transport/driving",
    mapDrivingRequest(request),
    { signal, timeout: 75_000, "axios-retry": { retries: 0 } },
  );
  return mapDrivingResult(response.data.data);
}

/** 查询高德步行距离。 */
export async function getWalkingRoute(request: WalkingRequest, signal?: AbortSignal): Promise<WalkingResult> {
  const response = await httpClient.post<WalkingResponseRaw>(
    "/itineraries/transport/walking",
    mapWalkingRequest(request),
    { signal },
  );
  return mapWalkingResult(response.data.data);
}

/** AI 客户端超时需要覆盖后端模型调用窗口。 */
const AI_REQUEST_TIMEOUT_MS = 35_000;

/** 点位批量补全需覆盖后端默认三次请求及 Retry-After 退避上限。 */
const POINT_DESCRIPTION_REQUEST_TIMEOUT_MS = 160_000;

/** 解析单日自然语言草稿。 */
export async function parseDayWithAi(
  itineraryId: string,
  request: ParseDayRequest,
  signal?: AbortSignal,
  draftDemandId?: string,
): Promise<ParsedDayDraft> {
  const response = await httpClient.post<ParsedDayResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/ai/parse-day`,
    mapParseDayRequest(request),
    { ...(draftDemandId ? { params: draftParams(draftDemandId) } : {}), signal, timeout: AI_REQUEST_TIMEOUT_MS },
  );
  return mapParsedDay(response.data.data);
}

/** 生成待确认的行程文案建议。 */
export async function enrichItinerary(
  itineraryId: string,
  request: EnrichRequest,
  signal?: AbortSignal,
  draftDemandId?: string,
): Promise<EnrichedContent> {
  const response = await httpClient.post<EnrichedContentResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/ai/enrich`,
    mapEnrichRequest(request),
    { ...(draftDemandId ? { params: draftParams(draftDemandId) } : {}), signal, timeout: AI_REQUEST_TIMEOUT_MS },
  );
  return mapEnrichedContent(response.data.data);
}

/** 补全指定范围内缺失的点位介绍。 */
export async function completePointDescriptions(
  itineraryId: string,
  request: PointDescriptionCompletionRequest,
  signal?: AbortSignal,
  draftDemandId?: string,
): Promise<PointDescriptionCompletionResult> {
  const response = await httpClient.post<PointDescriptionCompletionResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/ai/point-descriptions`,
    mapPointDescriptionCompletionRequest(request),
    { ...(draftDemandId ? { params: draftParams(draftDemandId) } : {}), signal, timeout: POINT_DESCRIPTION_REQUEST_TIMEOUT_MS },
  );
  return mapPointDescriptionCompletionResult(response.data.data);
}

/** 创建只读分享链接。 */
export async function createShareLink(itineraryId: string, viewType: "detail" | "table" = "detail"): Promise<ShareLinkResult> {
  const response = await httpClient.post<ShareLinkResponseRaw>(
    `/itineraries/${encodeURIComponent(itineraryId)}/share-link`,
    undefined,
    { params: { view_type: viewType }, "axios-retry": { retries: 0 } },
  );
  return mapShareLink(response.data.data);
}

/** 读取确认件提供的团号与酒店客户输出。 */
export async function getItineraryCustomerOutput(
  itineraryId: string,
  signal?: AbortSignal,
): Promise<ItineraryCustomerOutput> {
  const response = await httpClient.get<ItineraryCustomerOutputResponseRaw>(
    `/fulfillment-orders/itineraries/${encodeURIComponent(itineraryId)}/customer-output`,
    { signal },
  );
  return mapItineraryCustomerOutput(response.data.data);
}

/** 获取公开脱敏行程。 */
export async function getPublicItinerary(token: string): Promise<PublicItinerary> {
  const encodedToken = encodeURIComponent(token);
  const [response, customerOutputResponse] = await Promise.all([
    anonymousHttpClient.get<PublicItineraryResponseRaw>(`/itineraries/public/${encodedToken}`, { withCredentials: true }),
    anonymousHttpClient.get<ItineraryCustomerOutputResponseRaw>(
      `/fulfillment-orders/public/itineraries/${encodedToken}/customer-output`, { withCredentials: true },
    ),
  ]);
  return {
    ...mapPublicItinerary(response.data.data),
    ...mapItineraryCustomerOutput(customerOutputResponse.data.data),
  };
}

/** 草稿需求仅传给编辑及建议接口，不授权客户输出或状态写入。 */
function draftParams(draftDemandId?: string) {
  return draftDemandId ? { draft_demand_id: draftDemandId } : undefined;
}

/** 将未保存需求绑定到现有编辑器请求，不缓存任何服务端行程记录。 */
export function unsavedDemandRepo(demandId: string) {
  return {
    getEditor: (id: string) => getEditor(id, demandId),
    createMainData: (id: string, request: Parameters<typeof createMainData>[1]) => createMainData(id, request, demandId),
    saveEditor: (id: string, request: ItinerarySaveRequest) => saveEditor(id, request, demandId),
    validateEditorDraft: (id: string, editor: ItineraryEditor) => validateEditorDraft(id, editor, demandId),
    parseDayWithAi: (id: string, request: ParseDayRequest, signal?: AbortSignal) => parseDayWithAi(id, request, signal, demandId),
    enrichItinerary: (id: string, request: EnrichRequest, signal?: AbortSignal) => enrichItinerary(id, request, signal, demandId),
    completePointDescriptions: (id: string, request: PointDescriptionCompletionRequest, signal?: AbortSignal) => completePointDescriptions(id, request, signal, demandId),
  };
}
